import { useFocusable } from '../focus';
import { IconLock, IconNote } from '../icons';
import { colorFor } from '../styles/tokens';
import { Badge, type BadgeTone } from './Badge';
import styles from './Card.module.css';
import { CoverImage } from './CoverImage';
import { cx } from './cx';

export interface CardBadge {
  label: string;
  tone: Exclude<BadgeTone, 'count' | 'count-inverted'>;
}

export interface CardProps {
  /** `song` (25 px radius, 28 px title) or `playlist` (29 px radius, 30 px title). */
  variant?: 'song' | 'playlist';
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  /** Flat fallback color; derived from `title` when omitted. */
  color?: string;
  badge?: CardBadge;
  /** Lock overlay (feature gating comes from the DTO, docs/04 §3). */
  locked?: boolean;
  /** Cover edge in px (Figma: 220). */
  size?: number;
  focusKey?: string;
  onSelect?: () => void;
  onFocus?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function Card({
  variant = 'song',
  title,
  subtitle,
  imageUrl,
  color,
  badge,
  locked = false,
  size = 220,
  focusKey,
  onSelect,
  onFocus,
  className,
  ...rest
}: CardProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    onEnterPress: () => onSelect?.(),
    onFocus: () => onFocus?.(),
  });
  return (
    <div
      ref={ref}
      role="button"
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={rest['data-testid']}
      className={cx(styles.card, styles[variant], focused && styles.focused, className)}
      style={{ width: size }}
    >
      <div
        className={styles.cover}
        style={{ width: size, height: size, background: color ?? colorFor(title) }}
      >
        <CoverImage
          src={imageUrl}
          width={size}
          height={size}
          fallback={<IconNote size={Math.round(size * 0.42)} className={styles.note} />}
        />
        {badge ? (
          <Badge tone={badge.tone} className={styles.badge}>
            {badge.label}
          </Badge>
        ) : null}
        {locked ? (
          <span className={styles.lock} data-testid="card-lock">
            <IconLock size={32} />
          </span>
        ) : null}
      </div>
      <div className={styles.title}>{title}</div>
      {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
    </div>
  );
}
